import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ClickableDropdown, DropdownContext } from "../../design_system/components/Dropdown/Dropdown.jsx";
import { DropdownItem } from "../../design_system/components/Dropdown/DropdownItem.jsx";
import { DropdownMenu } from "../../design_system/components/Dropdown/DropdownMenu.jsx";
import { Icons } from "../../design_system/components/Icons";
import { SelectionControl } from "../../design_system/components/SelectionControls/SelectionControl.jsx";
import { TabGroup } from "../../design_system/components/Tabs/TabGroup.jsx";
import { TabItem } from "../../design_system/components/Tabs/TabItem.jsx";
import { Tooltip } from "../../design_system/components/ToolTip/ToolTip.jsx";
import { Loader } from "../../design_system/components/Loader.jsx";
import ConditionalRender from "../../ConditionalRender.js";
import { team_members } from "../../CommonApis.js";
import { indexOfObject } from "../utils/ArrayUtils.js";
import { useSelector } from "react-redux";
import { IconButton } from "../../design_system/components/Button/Button.jsx";

const ReviewedByTrigger = ({ label, onClick }) => {
    const { open } = useContext(DropdownContext);
    return (
        <div className="flex items-center cursor-pointer group-filter__reviewed-trigger" onClick={onClick}>
            <div className="group-filter__reviewed-link body-m-bold">{label}</div>
            <Icons name={open ? "chevron-up" : "chevron-down"} size={16} />
        </div>
    );
};

const ReviewedByItem = ({ children, onSelect }) => {
    const { setOpen } = useContext(DropdownContext);
    return (
        <DropdownItem onClick={(e) => { e.stopPropagation(); onSelect(); setOpen(false); }}>
            {children}
        </DropdownItem>
    );
};

const ClearFilterButton = ({ clearFilterCallBack }) => {
    const { close } = useContext(DropdownContext);
    return (
        <div 
            className="body-s-semibold link-default" 
            onClick={(e) => { 
                e.stopPropagation(); 
                clearFilterCallBack?.(close); 
            }}
        >
            Clear
        </div>
    );
};

const NewDropdownGroupFilter = ({
    group_options,
    filterList,
    onChangeCallback,
    customContainerClass,
    customStyle,
    positionRight = false,
    outerDropdownStyle,
    clearFilterCallBack,
    clearEnable = true,
    IconName = "filters",
    align = "left",
    ...props
}) => {
    const current_user = useSelector(state => state.current_user.user)
    const [activeOption, setActiveOption] = useState(group_options[0].group_key);
    const [dropDownData, setDropDownData] = useState({ team_users: [], loaders: { membersLoading: false } })
    const handleOptionToggle = (e, group) => {
        e.stopPropagation();
        setActiveOption(group.group_key)
    }
    const { team_users, loaders } = dropDownData
    const setState = (payload) => setDropDownData({ ...dropDownData, ...payload })
    const fetchTeamMembers = () => {
        setState({ loaders: { membersLoading: true } })
        team_members({
            approval_status: 'accepted',
            page_size: 100,
            from_filters: true
        }).then((res) => {
            let team_members = res.data.data || []
            let team_users = team_members.filter(member => member.user.id !== current_user.id)
            if (!current_user.is_publisher) {
                team_users = team_users.filter(member => !member.user.is_super_member);
            }
            team_users = team_users.map(member => ({ id: member.user.id, name: member.user.name }))
            if (team_users.length > 0) setState({ team_users: team_users, membersLoading: false });
        })
            .catch((err) => {
                setState({ loaders: { membersLoading: false } })
                console.log(
                    err.data
                );
            });
    }
    const current_reviewed_by = useCallback((reviewOption) => {
        let tempFilterOptions = [...(reviewOption?.filter_options || [])]
        let temp_reviewers = [...tempFilterOptions, ...team_users]
        let index = indexOfObject(temp_reviewers, "id", filterList.review)
        return index !== -1 ? temp_reviewers[index] : temp_reviewers[0]
    }, [team_users, filterList.review])

    const renderGroupOptionTabs = () => {
        let activeGroup = group_options.find(group => group.group_key === activeOption)

        return (
            <div>
                <TabGroup variant="line" gap="24" customClass="px-xl mb-l">
                    {group_options.map((group) => {
                        const isChanged = filterList[group.group_key] !== group.options[0].key;
                        return (
                            <TabItem
                                key={group.group_key}
                                label={group.group_label}
                                isActive={group.group_key === activeOption}
                                onClick={(e) => handleOptionToggle(e, group)}
                                style={isChanged ? { color: 'var(--color-action-primary)' } : undefined}
                            />
                        );
                    })}
                </TabGroup>
                {
                    activeGroup.options.map((option, index) => {
                        let is_chosen = option.dynamic ? filterList[activeGroup.group_key] === current_reviewed_by(option)?.id : filterList[activeGroup.group_key] === option.key
                        return (
                            <div key={option.key} className="flex items-center group-filter__option-row" style={{ marginBottom: 'var(--space-l)' }}>
                                <SelectionControl type="radio" key={option.key} label={option.label}
                                    checked={is_chosen}
                                    onChange={() => onChangeCallback(activeGroup.group_key, option.key)}
                                    style={{ marginTop: 0 }}
                                />
                                &nbsp;
                                <ConditionalRender if={option.dynamic}>
                                    <ClickableDropdown>
                                        <ReviewedByTrigger
                                            label={current_reviewed_by(option)?.name.split(" ")[0]}
                                            onClick={() => { if (team_users.length === 0) fetchTeamMembers(); }}
                                        />
                                        <DropdownMenu usePortal align={align} customStyle={{ maxHeight: 189, overflow: 'auto', minWidth: 134, padding: 'var(--space-s)' }} innerStyle={{ padding: '0' }}>
                                            {(option.filter_options || []).map((filter_option) => (
                                                <ReviewedByItem key={filter_option.id} onSelect={() => onChangeCallback(activeGroup.group_key, filter_option.id)}>
                                                    <Tooltip
                                                        tooltipText={() => (<div className="group-filter__tooltip-text">{filter_option.tooltipText}</div>)}
                                                        showWithoutOverflowing={true}
                                                        placement="left"
                                                        customStyle={{ width: "200px" }}
                                                    >
                                                        <span>{filter_option.name}</span>
                                                    </Tooltip>
                                                </ReviewedByItem>
                                            ))}
                                            <div className="group-filter__divider mt-s mb-s"></div>
                                            {loaders.membersLoading
                                                ? <div className="flex justify-center group-filter__loader"><Loader isLoading={true} /></div>
                                                : team_users.map((user) => (
                                                    <ReviewedByItem key={user.id} onSelect={() => onChangeCallback(activeGroup.group_key, user.id)}>
                                                        {user.name}
                                                    </ReviewedByItem>
                                                ))
                                            }
                                        </DropdownMenu>
                                    </ClickableDropdown>
                                </ConditionalRender>
                            </div>
                        )
                    })
                }
            </div>
        )
    }
    const filterApplied = useMemo(() => {
        return appliedFilterCondition(group_options, filterList);
    }, [filterList])

    return (
        <div>
            <ClickableDropdown>
                <Tooltip
                    tooltipText={() => 'Filters'}
                    placement="bottom"
                    showWithoutOverflowing={true}
                >
                    <IconButton
                        Size="Small"
                        Type="Tertiary"
                        IconName={IconName}
                        IsSelected={filterApplied > 0}
                    />
                </Tooltip>
                <DropdownMenu usePortal={true} innerStyle={{ padding: "0" }} customStyle={{ width: 250, marginTop: 'var(--space-xs)', ...outerDropdownStyle }}>
                    <div onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center group-filter__header">
                            <div className="body-l-bold group-filter__title">More Filters</div>
                            <ConditionalRender if={filterApplied && clearEnable}>
                                <ClearFilterButton clearFilterCallBack={clearFilterCallBack} />
                            </ConditionalRender>
                        </div>
                        <div className="group-filter__divider"></div>
                        {renderGroupOptionTabs()}
                    </div>
                </DropdownMenu>
            </ClickableDropdown>
        </div>
    )
}
//propTypes = { group_options, filterList, setOption, customStyle}
export const appliedFilterCondition = (group_options, filterList) => {
    return group_options.some(group => {
        const defaultValue = group.default_option ?? 'all';
        return filterList[group.group_key] !== defaultValue;
    });
}
export default NewDropdownGroupFilter
